import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const CONTACT_BG = "https://images.pexels.com/photos/5971175/pexels-photo-5971175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTEURS = [
  "Commercial",
  "Industriel",
  "Institutionnel",
  "Santé",
  "Résidentiel",
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    secteur: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/soumissions`, formData);
      setSubmitted(true);
      setFormData({ nom: "", email: "", telephone: "", secteur: "", message: "" });
    } catch (err) {
      setError("Une erreur est survenue. Veuillez réessayer ou nous appeler directement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden"
    >
      {/* Dark info section */}
      <div className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <img src={CONTACT_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0F1C]/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Contact Info + Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#e62e37] text-sm uppercase tracking-[0.25em] font-bold mb-4">
                Contactez-nous
              </p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-bold text-white mb-6 leading-tight">
                Parlons de
                <br />
                votre projet
              </h2>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-10 max-w-md">
                Notre équipe est disponible pour évaluer vos besoins en toiture et
                imperméabilisation. Contactez-nous pour obtenir une soumission gratuite.
              </p>

              {/* Contact details */}
              <div className="space-y-6 mb-10">
                <a href="tel:5147176662" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-[#e62e37] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Téléphone</p>
                    <p className="text-white font-bold text-xl group-hover:text-[#0077cc] transition-colors">514-717-6662</p>
                  </div>
                </a>

                <div className="flex items-center gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#0077cc] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Adresse</p>
                    <p className="text-white font-bold text-base">3361 Rue Chénier 101<br />Mirabel, QC</p>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-testid="contact-hours">
                  <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Heures d'ouverture</p>
                    <p className="text-white font-bold text-base">Lundi - Vendredi : 7h à 17h</p>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div data-testid="contact-map" className="w-full h-[250px] border border-white/10">
                <iframe
                  title="Emplacement TPL"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2786.8!2d-74.0!3d45.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM0LCsDM5JzAwLjAiTiA3NMKwMDAnMDAuMCJX!5e0!3m2!1sfr!2sca!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>

            {/* Right: Soumission Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2 tracking-tight">
                  Demande de soumission
                </h3>
                <p className="text-[#475569] text-sm mb-8">
                  Remplissez le formulaire et nous vous contacterons rapidement.
                </p>

                {submitted ? (
                  <div data-testid="form-success" className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0F172A] mb-2">Merci!</h4>
                    <p className="text-[#475569]">Votre demande a été envoyée avec succès. Nous vous contacterons sous peu.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      data-testid="form-new-request"
                      className="mt-6 text-[#0077cc] font-semibold text-sm underline hover:text-[#005fa3]"
                    >
                      Envoyer une autre demande
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="soumission-form" className="space-y-5">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        required
                        value={formData.nom}
                        onChange={handleChange}
                        data-testid="form-nom"
                        className="w-full px-4 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm focus:outline-none focus:border-[#0077cc] transition-colors"
                        placeholder="Votre nom"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                          Courriel *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          data-testid="form-email"
                          className="w-full px-4 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm focus:outline-none focus:border-[#0077cc] transition-colors"
                          placeholder="votre@courriel.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="telephone" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                          Téléphone *
                        </label>
                        <input
                          type="tel"
                          id="telephone"
                          name="telephone"
                          required
                          value={formData.telephone}
                          onChange={handleChange}
                          data-testid="form-telephone"
                          className="w-full px-4 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm focus:outline-none focus:border-[#0077cc] transition-colors"
                          placeholder="514-000-0000"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="secteur" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                        Secteur *
                      </label>
                      <select
                        id="secteur"
                        name="secteur"
                        required
                        value={formData.secteur}
                        onChange={handleChange}
                        data-testid="form-secteur"
                        className="w-full px-4 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm focus:outline-none focus:border-[#0077cc] transition-colors bg-white"
                      >
                        <option value="">Sélectionnez un secteur</option>
                        {SECTEURS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                        Décrivez votre projet *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="form-message"
                        className="w-full px-4 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm focus:outline-none focus:border-[#0077cc] transition-colors resize-none"
                        placeholder="Décrivez brièvement votre projet de toiture..."
                      />
                    </div>

                    {error && (
                      <p data-testid="form-error" className="text-[#e62e37] text-sm">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="form-submit"
                      className="w-full flex items-center justify-center gap-2 bg-[#e62e37] text-white font-bold uppercase tracking-wide px-8 py-4 text-sm hover:bg-[#cc222b] transition-all duration-300 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Envoyer ma demande
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
